import { useEffect, useState } from "react";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PACKAGES } from "@/constants/testIds";

export default function Packages() {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(null); // package id currently loading
    const [currency, setCurrency] = useState("AED"); // AED | USD

    useEffect(() => {
        api.get("/packages").then((r) => setPackages(r.data.packages));
    }, []);

    const handleBuy = async (pkg) => {
        setLoading(pkg.id);
        try {
            const { data } = await api.post("/payments/checkout", {
                lookup_key: pkg.lookup_key,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            console.error(e);
            toast.error("Could not start checkout. Please try WhatsApp instead.");
            setLoading(null);
        }
    };

    return (
        <section id="packages" className="relative py-24 sm:py-32 border-t border-white/10">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
                    <div className="md:col-span-6">
                        <div className="eyebrow mb-4 flex items-center gap-3">
                            <span className="inline-block w-10 h-[2px] bg-brand" /> Investment
                        </div>
                        <h2 className="font-display font-medium tracking-[-0.04em] text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">
                            Pick the runway.<br />
                            <span className="text-white/40">We handle the</span> <span className="text-brand">flight.</span>
                        </h2>
                    </div>
                    <div className="md:col-span-6 flex md:justify-end items-center gap-4">
                        <span className="text-xs text-white/50 uppercase tracking-widest font-mono">Show in</span>
                        <div className="inline-flex border border-white/20">
                            <button
                                onClick={() => setCurrency("AED")}
                                data-testid="currency-aed"
                                className={`px-4 py-2 text-sm font-semibold transition-colors ${currency === "AED" ? "bg-brand text-white" : "text-white/70 hover:text-white"}`}
                            >
                                AED
                            </button>
                            <button
                                onClick={() => setCurrency("USD")}
                                data-testid="currency-usd"
                                className={`px-4 py-2 text-sm font-semibold transition-colors ${currency === "USD" ? "bg-brand text-white" : "text-white/70 hover:text-white"}`}
                            >
                                USD
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
                    {packages.map((pkg) => {
                        const isHighlight = pkg.highlight;
                        const price = currency === "AED" ? pkg.price_aed : pkg.price_usd;
                        const currencyLabel = currency === "AED" ? "AED" : "USD";

                        return (
                            <div
                                key={pkg.id}
                                data-testid={PACKAGES.card(pkg.id)}
                                className={`relative p-8 lg:p-10 flex flex-col ${
                                    isHighlight
                                        ? "bg-[#FAFAFA] text-black shadow-[0_0_60px_-10px_rgba(0,144,255,0.5)] lg:-my-6 lg:scale-[1.03]"
                                        : "bg-[#141414] border border-white/10 text-white hover:border-white/30 transition-colors"
                                }`}
                            >
                                {isHighlight && (
                                    <div className="absolute -top-3 left-8 inline-flex items-center gap-2 bg-brand text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1.5">
                                        <Sparkles className="w-3 h-3" /> Most Popular
                                    </div>
                                )}

                                <div className={`text-xs font-mono uppercase tracking-[0.3em] ${isHighlight ? "text-brand" : "text-brand"}`}>
                                    {pkg.tagline}
                                </div>

                                <h3 className={`mt-6 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-5xl ${isHighlight ? "text-black" : "text-white"}`}>
                                    {pkg.name}
                                </h3>

                                <div className="mt-8 flex items-baseline gap-2">
                                    <span className={`numeric-display font-semibold text-5xl sm:text-6xl ${isHighlight ? "text-black" : "text-white"}`}>
                                        {currency === "USD" ? "$" : ""}{price.toLocaleString()}
                                    </span>
                                    <span className={`text-sm font-mono ${isHighlight ? "text-black/50" : "text-white/50"}`}>
                                        {currencyLabel} / mo
                                    </span>
                                </div>

                                <div className={`mt-6 border-t ${isHighlight ? "border-black/10" : "border-white/10"} pt-6 space-y-3 flex-1`}>
                                    {pkg.features.map((f) => (
                                        <div key={f} className="flex items-start gap-3">
                                            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${isHighlight ? "text-brand" : "text-brand"}`} />
                                            <span className={`text-sm leading-snug ${isHighlight ? "text-black/80" : "text-white/80"}`}>{f}</span>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={() => handleBuy(pkg)}
                                    disabled={loading === pkg.id}
                                    data-testid={PACKAGES.buyBtn(pkg.id)}
                                    className={`mt-8 group inline-flex items-center justify-between gap-3 px-6 py-4 font-semibold text-sm transition-colors ${
                                        isHighlight
                                            ? "bg-brand hover:bg-brand-hover text-white"
                                            : "bg-white text-black hover:bg-brand hover:text-white"
                                    } disabled:opacity-60 disabled:cursor-not-allowed`}
                                >
                                    <span>{loading === pkg.id ? "Redirecting..." : "Buy this package"}</span>
                                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                </button>

                                <a
                                    href={`https://wa.me/971543676278?text=Hi%20Babur!%20I'm%20interested%20in%20the%20${encodeURIComponent(pkg.name)}%20package.`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`mt-3 text-xs text-center font-mono uppercase tracking-widest transition-colors ${
                                        isHighlight ? "text-black/50 hover:text-brand" : "text-white/50 hover:text-brand"
                                    }`}
                                >
                                    or WhatsApp instead →
                                </a>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-10 text-center text-xs font-mono text-white/40 uppercase tracking-widest">
                    * Advertising / boost spending, domain and hosting are not included in packages.
                </div>
            </div>
        </section>
    );
}
